import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export function CartProvider({ children }) {
  // Initialize cart from sessionStorage if available
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = sessionStorage.getItem('vencejo-cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });

  // Save cart to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('vencejo-cart', JSON.stringify(cart));
  }, [cart]);

  // Add item to cart (for Store products)
  const addToCart = (product, tutoringPack = null) => {
    const cartItemId = tutoringPack ? `${product.id}-${tutoringPack.id}` : `${product.id}`;
    const existingItem = cart.find(item => item.cartItemId === cartItemId);
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.cartItemId === cartItemId 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      const totalPrice = product.price + (tutoringPack ? tutoringPack.price : 0);
      setCart([...cart, { 
        ...product, 
        cartItemId,
        tutoringPack,
        totalPrice,
        quantity: 1,
        type: 'course' // To differentiate from game items
      }]);
    }
  };

  // Add game item to cart (for Game Hub)
  const addGameToCart = (game, planType, plan) => {
    const cartItemId = `game-${game.id}-${planType}`;
    const existingItem = cart.find(item => item.cartItemId === cartItemId);
    
    if (!existingItem) {
      setCart([...cart, {
        cartItemId,
        id: game.id,
        title: game.name,
        image: game.image,
        planType,
        price: plan.price,
        totalPrice: plan.price,
        period: plan.period,
        quantity: 1,
        type: 'game'
      }]);
    }
  };

  // Remove item from cart
  const removeFromCart = (cartItemId) => {
    setCart(cart.filter(item => item.cartItemId !== cartItemId));
  };

  // Update quantity
  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
    } else {
      setCart(cart.map(item => 
        item.cartItemId === cartItemId 
          ? { ...item, quantity }
          : item
      ));
    }
  };

  // Clear cart
  const clearCart = () => {
    setCart([]);
  };

  // Get total price
  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.totalPrice * item.quantity), 0);
  };

  // Get total items count
  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cart,
    addToCart,
    addGameToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export default CartContext;

